import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id]);

  return (
    <div className="min-h-screen" data-testid="order-success-page">
      <div className="max-w-[1000px] mx-auto px-3 md:px-6 py-8">
        <div className="card p-6 md:p-10">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="w-14 h-14 text-[#3a6a3a] shrink-0" strokeWidth={1.5} />
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#c8532e]">Confirmed</div>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#1a1d29] mt-2">
                Your order is placed.
              </h1>
              <p className="mt-2 text-sm text-[#6b6b6b] max-w-lg">
                A confirmation has been sent to your email. We'll be in touch when your pieces are on the way.
              </p>
            </div>
          </div>

          {order && (
            <div className="mt-10 border border-[#e0d8c8] rounded-md overflow-hidden" data-testid="order-summary-block">
              <div className="p-4 bg-[#faf7f2] border-b border-[#e0d8c8] grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6b6b6b]">Order ID</div>
                  <div className="mt-1 font-mono text-xs">{order.id.slice(0, 12)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6b6b6b]">Placed</div>
                  <div className="mt-1">{new Date(order.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6b6b6b]">Total</div>
                  <div className="mt-1 font-display text-xl font-semibold">{formatINR(order.amount)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6b6b6b]">Ship to</div>
                  <div className="mt-1">{order.shipping?.full_name}, {order.shipping?.city}</div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {order.items.map((it, i) => (
                  <div key={i} className="flex gap-4 text-sm">
                    <div className="w-20 h-24 bg-[#faf7f2] border border-[#e0d8c8] rounded-md overflow-hidden shrink-0">
                      {it.image && <img src={it.image} className="w-full h-full object-cover" alt="" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-[#6b6b6b] mt-0.5">
                        {it.size ? `Size ${it.size}` : ""}{it.size && it.color ? " · " : ""}{it.color || ""}
                      </div>
                      <div className="text-xs text-[#6b6b6b] mt-0.5">Qty {it.quantity}</div>
                    </div>
                    <div className="font-medium">{formatINR(it.line_total)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/shop" className="btn-primary text-sm" data-testid="success-continue-shopping">
              Continue shopping
            </Link>
            <Link to="/orders" className="ec-link text-sm" data-testid="success-view-orders">
              View my orders →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
